import { useIntl } from 'react-intl';
import { IconTooltip, Tag, Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import AboveBenchmarkTooltip from './AboveBenchmarkTooltip';

import type { ReactNode } from 'react';

type Props = {
  /** Campaign on-target share (0-100, may exceed 100). Null when unknown. */
  onTargetPct: number | null;
  /** On-target share of an untargeted plan with the same spend (the benchmark). Null when unknown. */
  baselineOnTargetPct: number | null;
  /** Percentage points between the campaign and the benchmark. Null when unknown. */
  onTargetVsBaselinePts: number | null;
  /** On-target impressions, quoted by the "above avg." tag's tooltip. */
  targetImpacts: number | null;
  benchmarkTooltip: ReactNode;
};

/** Bars are drawn against a 0-100 track; a share above 100 fills it. */
const toBarWidth = (pct: number | null): string => `${Math.min(100, Math.max(0, pct ?? 0))}%`;

/**
 * Highlighted panel of the on-target card: the campaign's on-target share against
 * the benchmark (an untargeted plan with the same spend), and how far above it sits.
 */
export default function OnTargetDetails({
  onTargetPct,
  baselineOnTargetPct,
  onTargetVsBaselinePts,
  targetImpacts,
  benchmarkTooltip
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatPercent } = useDoohReportFormatters();
  const isAboveBenchmark = onTargetVsBaselinePts !== null && onTargetVsBaselinePts > 0;

  return (
    <div className="metricDetailCards__onTarget">
      {isAboveBenchmark && (
        <span className="metricDetailCards__onTargetAverage">
          <Tooltip
            config={{
              component: (
                <AboveBenchmarkTooltip
                  baselineOnTargetPct={baselineOnTargetPct}
                  onTargetPct={onTargetPct}
                  onTargetVsBaselinePts={onTargetVsBaselinePts}
                  targetImpacts={targetImpacts}
                />
              ),
              interactions: [TooltipInteractionEnum.hover],
              placement: 'top',
              portal: true,
              strategy: 'fixed'
            }}
          >
            <span>
              <Tag
                icon={{ position: 'left', webFont: 'fa-regular fa-arrow-trend-up' }}
                id="metricDetailCards-benchmark"
                status="ok"
              >
                {formatMessage(
                  { defaultMessage: '{pct} above avg.', id: 'doohReport.metricDetailCards.aboveBenchmark' },
                  { pct: formatPercent(onTargetVsBaselinePts, 2) }
                )}
              </Tag>
            </span>
          </Tooltip>
        </span>
      )}
      <span className="metricDetailCards__onTargetPct">
        {formatMessage(
          { defaultMessage: '{pct} on target', id: 'doohReport.metricDetailCards.onTargetShare' },
          { pct: formatPercent(onTargetPct) }
        )}
      </span>
      <div className="metricDetailCards__comparison">
        <span className="metricDetailCards__comparisonLabel">
          {formatMessage({ defaultMessage: 'Your campaign', id: 'doohReport.metricDetailCards.yourCampaign' })}
        </span>
        <div className="metricDetailCards__comparisonTrack">
          <div
            className="metricDetailCards__comparisonBar metricDetailCards__comparisonBar--campaign"
            style={{ width: toBarWidth(onTargetPct) }}
          />
        </div>
        <span className="metricDetailCards__comparisonValue metricDetailCards__comparisonValue--campaign">
          {formatPercent(onTargetPct, 2)}
        </span>

        <span className="metricDetailCards__comparisonLabel">
          {formatMessage({ defaultMessage: 'Vs Benchmark', id: 'doohReport.metricDetailCards.vsBenchmark' })}
          <IconTooltip
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
            place="top"
          >
            {benchmarkTooltip}
          </IconTooltip>
        </span>
        <div className="metricDetailCards__comparisonTrack">
          <div
            className="metricDetailCards__comparisonBar metricDetailCards__comparisonBar--benchmark"
            style={{ width: toBarWidth(baselineOnTargetPct) }}
          />
        </div>
        <span className="metricDetailCards__comparisonValue">{formatPercent(baselineOnTargetPct)}</span>
      </div>
    </div>
  );
}
